import React, { FC } from "react";
import { Helmet } from "react-helmet";
import OnboardingShell from "./OnboardingShell";
import placeholderImg from "images/placeholder-large.png";
import { Dictionary } from "./i18n/strings";
import { useLocale } from "./i18n/locale";

export interface TenantWelcomeHomePageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

const getSummary = (t: Dictionary) => [
  t.home.identityVerified,
  t.home.contractSigned,
  t.home.inventoryConfirmed,
  t.home.insuranceReceived,
];

// TODO: the support contact belongs to the DMS / Europea team, not the page.
// Values taken from the design mockup until that contract exists.
const SUPPORT = {
  phone: "+33 1 84 06 00 00",
  email: "concierge@europea-residences.com",
};

const TenantWelcomeHomePage: FC<TenantWelcomeHomePageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const { t } = useLocale();
  const summary = getSummary(t);

  return (
    <div
      className={`nc-TenantWelcomeHomePage ${className}`}
      data-nc-id="TenantWelcomeHomePage"
    >
      <Helmet>
        <title>{t.home.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={7}
        totalSteps={totalSteps}
        title={t.home.title}
        subtitle={t.home.subtitle}
        wide
        hideTitle
      >
        <div className="grid gap-5 sm:grid-cols-[1.4fr_1fr] sm:items-stretch">
          <div className="flex flex-col gap-3">
            <div className="text-center">
              <h1 className="font-serif font-semibold text-2xl leading-tight text-navy-900">
                {t.home.title}
              </h1>
              <p className="max-w-[300px] mx-auto mt-1.5 text-[13px] font-semibold leading-relaxed text-neutral-500">
                {t.home.subtitle}
              </p>
            </div>

            <div className="relative h-44 w-full min-h-0 overflow-hidden rounded-md sm:h-auto sm:flex-1">
          <img
            src={"/living-area.jpg"}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <section className="rounded-md border border-neutral-200 bg-white p-4">
              <h2 className="text-[14px] font-semibold text-navy-900">
                {t.home.summary}
              </h2>

              <ul className="mt-3 flex flex-col gap-2.5">
                {summary.map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <svg
                      className="h-4 w-4 flex-none text-green-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8 12l2.5 2.5L16 9" />
                    </svg>
                    <span className="text-[13px] text-neutral-700 font-semibold">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex items-center gap-2.5 border border-primary-6000/20 rounded-md bg-cream-100 px-3 py-2.5">
                <svg
                  className="h-7 w-7 flex-none text-primary-6000"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11.2v5.2" />
                  <path d="M12 7.9v.1" />
                </svg>
                <p className="text-xs leading-relaxed text-neutral-600 font-semibold">
                  {t.home.documentsEmailed}
                </p>
              </div>
            </section>

            {/* The support contact is hidden for every earlier step and revealed
            here — onboarding is complete (spec, global business rules). */}
            <section className="rounded-md bg-navy-800 p-4 text-white">
              <h2 className="text-[14px] font-semibold">{t.home.needHelp}</h2>

              <ul className="mt-3 flex flex-col gap-2.5">
                <li>
                  <a
                    href={`tel:${SUPPORT.phone.replace(/\s/g, "")}`}
                    className="font-semibold flex items-center gap-2.5 text-[13px] text-white/90 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  >
                    <svg
                      className="h-4 w-4 flex-none"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.4 2.1L8.1 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z" />
                    </svg>
                    {SUPPORT.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${SUPPORT.email}`}
                    className="font-semibold flex items-center gap-2.5 text-[12px] leading-snug text-white/90 transition-colors [overflow-wrap:anywhere] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  >
                    <svg
                      className="h-4 w-4 flex-none"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="M3 7l9 6 9-6" />
                    </svg>
                    {SUPPORT.email}
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </OnboardingShell>
    </div>
  );
};

export default TenantWelcomeHomePage;
